import React, { useEffect } from "react";
import CourseTemplate from "./CourseTemplate";
import SEO, { generateCourseSchema, generateFAQSchema } from "./SEO";

export default function AIWorkshopPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const courseModules = [
    {
      number: 1,
      title: "Introduction to AI Tools for Business",
      description:
        "Understand the landscape of AI tools available today and how they can transform your workflow.",
      duration: "1 hour",
      topics: [
        "Overview of AI tools landscape (ChatGPT, Gemini, Microsoft 365 Co-Pilot)",
        "How AI is transforming business operations",
        "Identifying opportunities for AI in your workflow",
        "Ethical considerations and best practices",
      ],
    },
    {
      number: 2,
      title: "Mastering ChatGPT for Business Applications",
      description:
        "Learn how to effectively use ChatGPT to enhance your productivity and streamline communication.",
      duration: "1.5 hours",
      topics: [
        "Crafting effective prompts for better results",
        "Using ChatGPT for content creation and editing",
        "Automating routine communications",
        "Research and information synthesis techniques",
        "Hands-on exercises with real business scenarios",
      ],
    },
    {
      number: 3,
      title: "Google Gemini: Advanced Features for Professionals",
      description:
        "Explore Google's Gemini and its unique capabilities for business applications.",
      duration: "1.5 hours",
      topics: [
        "Gemini's multimodal capabilities (text, images, code)",
        "Integrating Gemini with Google Workspace",
        "Data analysis and visualization with Gemini",
        "Comparing Gemini and ChatGPT: When to use each tool",
        "Practical applications across different business functions",
      ],
    },
    {
      number: 4,
      title: "Microsoft 365 Co-Pilot: Transforming Your Office Workflow",
      description:
        "Discover how Microsoft's AI assistant can revolutionize your daily tasks in Office applications.",
      duration: "1.5 hours",
      topics: [
        "Co-Pilot in Word: Advanced document creation and editing",
        "Co-Pilot in Excel: Data analysis and formula generation",
        "Co-Pilot in PowerPoint: Creating compelling presentations",
        "Co-Pilot in Outlook: Email management and response generation",
        "Co-Pilot in Teams: Meeting summaries and action items",
      ],
    },
    {
      number: 5,
      title: "Building an AI-Enhanced Workflow",
      description:
        "Create a personalized strategy for integrating AI tools into your daily work routine.",
      duration: "2 hours",
      topics: [
        "Identifying repetitive tasks for AI automation",
        "Creating custom workflows combining multiple AI tools",
        "Measuring productivity improvements",
        "Addressing challenges and limitations",
        "Future-proofing your skills in an AI-enhanced workplace",
        "Action planning workshop: Your 30-day AI implementation plan",
      ],
    },
  ];

  const courseSchema = generateCourseSchema({
    name: "Work Smarter with AI: Automate, Optimize & Stay Ahead",
    description:
      "A hands-on workshop to transform your daily workflow with AI tools. Learn to automate tasks, enhance decision-making, and boost productivity using cutting-edge AI solutions.",
    provider: "One80Labs",
    url: "https://one80labs.com/ai-workshop",
    image: "https://images.unsplash.com/photo-1591453089816-0fbb971b454c",
    price: 997,
    duration: "P1D",
  });

  // Advanced AEO-optimized FAQ questions for AI answer engines
  const aeoFaqQuestions = [
    {
      question: "What is the One80Labs AI workshop?",
      answer:
        "The One80Labs AI workshop is a comprehensive 8-hour intensive training session that teaches professionals how to use AI tools like ChatGPT, Gemini, and Microsoft 365 Co-Pilot to automate tasks, enhance productivity, and streamline business workflows. The workshop is designed for beginners to intermediate users and includes hands-on exercises with real business scenarios.",
    },
    {
      question: "How much does the One80Labs AI workshop cost?",
      answer:
        "The One80Labs AI workshop costs $997 for the full 8-hour intensive training session. This includes comprehensive training on ChatGPT, Gemini, and Microsoft 365 Co-Pilot, hands-on exercises, course materials, and a 30-day AI implementation plan. The workshop is limited to 20 participants to ensure personalized attention.",
    },
    {
      question: "Where is the One80Labs AI workshop held?",
      answer:
        "The One80Labs AI workshop is held in Omaha, Nebraska, at our training facility. We also offer virtual sessions for participants who cannot attend in person. The workshop serves professionals throughout the Midwest region including Nebraska, Iowa, Kansas, Missouri, and South Dakota.",
    },
    {
      question: "What AI tools are covered in the One80Labs workshop?",
      answer:
        "The One80Labs AI workshop covers three main AI tools: ChatGPT for content creation and communication automation, Google Gemini for multimodal tasks and data analysis, and Microsoft 365 Co-Pilot for Office application integration. The workshop teaches when and how to use each tool effectively for different business applications.",
    },
    {
      question:
        "Do I need technical skills to attend the One80Labs AI workshop?",
      answer:
        "No technical background is required to attend the One80Labs AI workshop. The training is designed for beginners to intermediate users and focuses on practical applications rather than technical implementation. You'll learn how to use AI tools through hands-on exercises and real business scenarios that you can immediately apply to your work.",
    },
    {
      question: "How do I register for the One80Labs AI workshop?",
      answer:
        "To register for the One80Labs AI workshop, contact us at hello@one80labs.com or call +1-402-915-3616. You can also visit our website to schedule a consultation and discuss your specific training needs. The workshop is limited to 20 participants, so early registration is recommended to secure your spot.",
    },
    {
      question: "What will I learn in the One80Labs AI workshop?",
      answer:
        "In the One80Labs AI workshop, you'll learn to craft effective prompts for better AI results, automate routine communications and tasks, integrate AI tools with your existing workflows, analyze data and create visualizations, generate reports and presentations, and build a personalized 30-day AI implementation plan for your business.",
    },
    {
      question: "Is the One80Labs AI workshop suitable for my business?",
      answer:
        "The One80Labs AI workshop is suitable for professionals across all industries who want to improve productivity and streamline workflows. Whether you're in finance, healthcare, manufacturing, retail, or any other sector, the AI tools and techniques taught in the workshop can be adapted to your specific business needs and challenges.",
    },
    // Advanced AEO - Tool-specific questions
    {
      question: "What is ChatGPT and how can it help my business?",
      answer:
        "ChatGPT is an AI language model that can help Omaha businesses with content creation, customer service automation, data analysis, and workflow optimization. Our AI workshop teaches you how to write effective prompts, automate routine tasks, and integrate ChatGPT into your daily operations. ChatGPT can generate emails, reports, marketing copy, and answer customer questions 24/7.",
    },
    {
      question: "What is Google Gemini and how does it differ from ChatGPT?",
      answer:
        "Google Gemini is a multimodal AI model that can process text, images, and code simultaneously. Unlike ChatGPT, Gemini can analyze images, create visual content, and work with Google Workspace applications. Our AI workshop covers both ChatGPT and Gemini, helping you choose the right tool for specific tasks. Gemini excels at data analysis, visual content creation, and Google Workspace integration.",
    },
    {
      question:
        "What is Microsoft 365 Copilot and how can it improve productivity?",
      answer:
        "Microsoft 365 Copilot is an AI assistant integrated into Office applications that can help Omaha businesses create documents, analyze data, generate presentations, and manage emails more efficiently. Our Microsoft 365 Copilot training teaches you how to use AI to automate Excel formulas, create PowerPoint presentations, draft emails, and summarize meetings. Copilot can save 2-3 hours per day for typical office workers.",
    },
    {
      question: "How do I write effective prompts for AI tools?",
      answer:
        "Writing effective prompts involves being specific, providing context, and iterating on results. Our AI workshop teaches prompt engineering techniques including role specification, output formatting, and iterative refinement. We cover best practices for ChatGPT, Gemini, and Microsoft 365 Copilot to help you get the most accurate and useful responses from AI tools.",
    },
    {
      question: "How can I integrate AI tools with my existing software?",
      answer:
        "AI tools can be integrated with existing software through APIs, plugins, and workflow automation. Our AI workshop covers integration strategies for popular business software including CRM systems, project management tools, and communication platforms. We teach you how to create automated workflows that connect AI tools with your current technology stack.",
    },
    // Advanced AEO - Industry-specific questions
    {
      question: "How can AI help healthcare professionals in Omaha?",
      answer:
        "AI can help Omaha healthcare professionals with patient scheduling, medical documentation, diagnostic assistance, and administrative automation. Our AI workshop for healthcare covers HIPAA-compliant AI tools, patient communication automation, and medical record management. AI can reduce administrative burden by 30-40% while improving patient care quality.",
    },
    {
      question: "How can AI benefit manufacturing companies in Nebraska?",
      answer:
        "AI can help Nebraska manufacturing companies with predictive maintenance, quality control, supply chain optimization, and production planning. Our AI workshop for manufacturers covers IoT integration, predictive analytics, and automated quality inspection. AI can reduce downtime by 25% and improve production efficiency by 15-20%.",
    },
    {
      question:
        "How can AI help financial services companies in Council Bluffs?",
      answer:
        "AI can help Council Bluffs financial services companies with fraud detection, customer service automation, risk assessment, and compliance monitoring. Our AI workshop for financial professionals covers automated document processing, customer chatbot implementation, and regulatory compliance automation. AI can improve fraud detection accuracy by 90% and reduce processing time by 60%.",
    },
    {
      question: "How can AI help retail businesses in the Midwest?",
      answer:
        "AI can help Midwest retail businesses with inventory management, customer personalization, demand forecasting, and automated marketing. Our AI workshop for retail professionals covers customer behavior analysis, automated email campaigns, and inventory optimization. AI can increase sales by 15-25% and reduce inventory costs by 20%.",
    },
    // Advanced AEO - Implementation questions
    {
      question: "How long does it take to implement AI in my business?",
      answer:
        "AI implementation timeline varies by business size and complexity. Simple AI tools like ChatGPT can be implemented in 1-2 weeks, while comprehensive AI systems may take 2-3 months. Our AI workshop helps identify quick wins and long-term opportunities. Most businesses see ROI within 30-60 days of AI implementation.",
    },
    {
      question: "What is the ROI of AI implementation for small businesses?",
      answer:
        "Small businesses typically see 200-300% ROI on AI implementation within 6-12 months. Our workshop participants report 20-40% time savings, 15-25% cost reductions, and 30-50% productivity improvements. Our training helps identify specific ROI opportunities for your business type and size.",
    },
    {
      question: "How do I choose the right AI tools for my business?",
      answer:
        "Choosing the right AI tools depends on your business needs, budget, and technical capabilities. Our AI workshop analyzes your current processes and identifies the most impactful AI solutions. We recommend starting with user-friendly tools like ChatGPT and Microsoft 365 Copilot, then scaling to more advanced solutions as your team becomes comfortable with AI.",
    },
    {
      question: "What are the security concerns with AI implementation?",
      answer:
        "AI security concerns include data privacy, intellectual property protection, and compliance requirements. Our AI workshop covers security best practices, data handling protocols, and compliance considerations. We help businesses implement AI safely while maintaining data security and regulatory compliance. Most AI tools offer enterprise-grade security features.",
    },
    {
      question: "How can I measure the success of AI implementation?",
      answer:
        "Measure AI success through key performance indicators including time savings, cost reductions, productivity improvements, and quality enhancements. Our AI workshop teaches you how to establish baseline metrics, track improvements, and calculate ROI. We provide templates and frameworks for measuring AI impact in your specific business context.",
    },
    {
      question: "What are the common challenges when implementing AI?",
      answer:
        "Common AI implementation challenges include resistance to change, data quality issues, integration complexity, and unrealistic expectations. Our AI workshop addresses these challenges by teaching change management strategies, data preparation techniques, and realistic goal setting. We help you anticipate and overcome common obstacles to successful AI adoption.",
    },
  ];

  const faqSchema = generateFAQSchema(aeoFaqQuestions);

  return (
    <>
      <SEO
        title="AI Workshop - Work Smarter with AI | One80Labs"
        description="Transform your workflow with AI tools like ChatGPT, Gemini, and Microsoft 365 Co-Pilot. Hands-on training to boost productivity and streamline operations."
        image="https://images.unsplash.com/photo-1591453089816-0fbb971b454c"
        jsonLd={[courseSchema, faqSchema]}
        keywords={[
          "AI workshop",
          "ChatGPT training",
          "Gemini training",
          "Microsoft 365 Copilot",
          "AI productivity",
          "workflow automation",
          "business AI",
          "AI implementation",
        ]}
      />
      <CourseTemplate
        title="Work Smarter with AI: Automate, Optimize & Stay Ahead"
        subtitle="A hands-on workshop to transform your daily workflow with AI tools"
        description="Discover how AI tools like ChatGPT, Gemini, and Microsoft 365 Co-Pilot can transform your daily workflow. In this hands-on workshop, you'll learn to automate tasks, enhance decision-making, and boost productivity using cutting-edge AI solutions. Walk away with practical skills to work smarter, not harder—no tech background required!"
        imageUrl="https://images.unsplash.com/photo-1591453089816-0fbb971b454c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
        duration="1-Day Intensive Workshop (8 hours)"
        level="Beginner to Intermediate"
        students="Limited to 20 participants"
        modules={courseModules}
      />
    </>
  );
}
